import { ForwardedRef, useMemo } from "react";
import { View } from "react-native";
import { GestureDetector } from "react-native-gesture-handler";
import { useSharedValue } from "react-native-reanimated";
import getMaxInsideBounds from "src/helpers/getMaxInsideBounds";
import { ImageCropperHandler, ImageCropperProps } from "../../types";
import CutoutOverlay from "./components/CutoutOverlay";
import ImageCropperImageDisplay from "./components/ImageCropperImageDisplay";
import ScaleSlider from "./components/ScaleSlider";
import { ImageCropperMaxScale, ImageCropperMinScale } from "./constants";
import useImageCropperGesture from "./hooks/useImageCropperGesture";
import useImageCropperHandler from "./hooks/useImageCropperHandler";

const ImageCropperContent = ({
	uri,
	width,
	height,
	imageWidth,
	imageHeight,
	parentRef,
	shape = "Circle",
	initialScale = 1.1,
}: Omit<ImageCropperProps, "src" | "containerStyle" | "onImageLoadError"> & {
	uri: string;
	width: number;
	height: number;
	imageWidth: number;
	imageHeight: number;
	parentRef: ForwardedRef<ImageCropperHandler>;
}) => {
	const inside = useMemo(() => {
		return getMaxInsideBounds({
			width,
			height,
			aspectRatio: imageWidth / imageHeight,
		});
	}, [height, imageHeight, imageWidth, width]);
	const cropSV = useSharedValue({
		x: 0,
		y: 0,
		scale: Math.max(Math.min(initialScale, ImageCropperMaxScale), ImageCropperMinScale),
	});
	const { gesture } = useImageCropperGesture({
		cropSV,
		inside,
	});
	useImageCropperHandler({
		uri,
		cropSV,
		parentRef,
		image: { width: imageWidth, height: imageHeight },
		inside,
	});
	const size = Math.max(inside.width, inside.height);

	return (
		<>
			<GestureDetector gesture={gesture}>
				<View
					style={{
						width: size,
						height: size,
						overflow: "hidden",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<ImageCropperImageDisplay uri={uri} cropSV={cropSV} inside={inside} />
					<CutoutOverlay inside={inside} shape={shape} />
				</View>
			</GestureDetector>
			<ScaleSlider width={width} cropSV={cropSV} inside={inside} />
		</>
	);
};
export default ImageCropperContent;
